import CoursesGrid from "../CoursesGrid";
import React from "react";
import deltas from "../../../redux/actions/deltas";
import {connect} from "react-redux";
import SelectInput from "../../atoms/SelectInput";
import CheckboxField from "../../atoms/CheckboxField";
import {createSelector} from 'reselect';
import {
    GRID_FILTER_TYPE_DATE,
    GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD,
    GRID_FILTER_TYPE_BOOLEAN,
} from "../../molecules/GridFilter";
import Tippy from "@tippyjs/react";
import WarningIcon from "../../atoms/WarningIcon";

// Wraps a column's normal text rendering: if Course::validate() (backend) recorded a warning against
// fieldKey, shows it as an icon+tooltip before the text, in this same cell - not a separate column.
const withValidationWarningIcon = (fieldKey, renderText) => ({row}) => {
    const warnings = row.original.validationWarnings?.[fieldKey];
    // Falls back to '' rather than returning null/undefined from render: fields this column depends on
    // aren't always present, e.g. the provider-facing /courses response omits several CEC-only fields.
    const text = renderText(row.original) ?? '';

    return warnings?.length
        ? <span style={{display: 'flex', alignItems: 'center'}}>
            <Tippy content={warnings.join('; ')} placement="top" animation="fade" arrow theme="light-border">
                <span className="me-1"><WarningIcon/></span>
            </Tippy>
            {text}
          </span>
        : text;
};

export const allColumns = [
    {
        accessor: 'name',
        Header: 'Course Name',
        gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD,
        Cell: withValidationWarningIcon('name', row => row.name),
    },
    { accessor: 'providerName', Header: 'Provider', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    {
        id: 'regionNames',
        accessor: row => (row.regionNames || []).join(', '),
        Header: 'Regions',
        gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD,
        disableSortBy: true,
    },
    { accessor: 'startDate', Header: 'Start Date', gridFilterType: GRID_FILTER_TYPE_DATE },
    {
        accessor: 'endDate',
        Header: 'End Date',
        gridFilterType: GRID_FILTER_TYPE_DATE,
        Cell: withValidationWarningIcon('endDate', row => row.endDate),
    },
    { accessor: 'isActive', Header: 'Active', gridFilterType: GRID_FILTER_TYPE_BOOLEAN },
    {
        id: 'deliveryTypeNames',
        accessor: row => (row.deliveryTypeNames || []).join(', '),
        Header: 'Delivery Types',
        gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD,
        disableSortBy: true,
    },
    {
        id: 'costTypeNames',
        accessor: row => (row.costTypeNames || []).join(', '),
        Header: 'Payment Triggers',
        disableSortBy: true,
        Cell: withValidationWarningIcon('costTypeNames', row => (row.costTypeNames || []).join(', ')),
    },
    { accessor: 'isAccredited', Header: 'Accredited', gridFilterType: GRID_FILTER_TYPE_BOOLEAN },
    { accessor: 'isTopUp', Header: 'Top-Up', gridFilterType: GRID_FILTER_TYPE_BOOLEAN },
    { accessor: 'isItp', Header: 'ITP', gridFilterType: GRID_FILTER_TYPE_BOOLEAN },
    { accessor: 'isVisibleToCareersLeads', Header: 'Visible to Careers Leads', gridFilterType: GRID_FILTER_TYPE_BOOLEAN },
    {
        accessor: 'isExemptFromPayment',
        Header: 'Exempt From Payment',
        gridFilterType: GRID_FILTER_TYPE_BOOLEAN,
        Cell: withValidationWarningIcon('isExemptFromPayment', row => row.isExemptFromPayment),
    },
];

const {
    hideCoursesGridColumn,
    showCoursesGridColumn,
    showAllCoursesGridColumns,
    resetCoursesGridFilterAndSort,
} = deltas.actionCreators;

const getColumnsToShow = createSelector(
    state => state.coursesGrid.columnsToHide,
    () => allColumns,
    (columnsToHide, baseColumns) => baseColumns.filter(column => !columnsToHide.includes(column.id || column.accessor)),
);

const mapStateToProps = state => ({
    columnsToHide: state.coursesGrid.columnsToHide,
    columnsToShow: getColumnsToShow(state),
});

const mapDispatchToProps = dispatch => ({
    hideCoursesGridColumn: column => dispatch(hideCoursesGridColumn(column)),
    showCoursesGridColumn: column => dispatch(showCoursesGridColumn(column)),
    showAllCoursesGridColumns: () => dispatch(showAllCoursesGridColumns()),
    resetCoursesGridFilterAndSort: () => dispatch(resetCoursesGridFilterAndSort()),
});

const CoursesSection = ({columnsToHide, columnsToShow, resetCoursesGridFilterAndSort, hideCoursesGridColumn, showCoursesGridColumn, showAllCoursesGridColumns}) => {

    const formatOptionLabel = ({value, label}) => (
        <div>
            <CheckboxField
                label={label}
                checked={!columnsToHide.includes(value) && 'ALL' !== value}
            />
        </div>
    );

    return <React.Fragment>
        <div className="d-block d-md-flex mb-3 mt-4">
            <div className="col-md-3 position-relative px-0 no-padding me-2">
                <SelectInput
                    id="o-columns"
                    placeholder="Hide and show columns"
                    isMulti={true}
                    overrideOnChange={(selectedOptions, {option: {value}}) => {
                        if (value === 'ALL') {
                            showAllCoursesGridColumns();
                            return;
                        }

                        !columnsToHide.includes(value)
                            ? hideCoursesGridColumn(value)
                            : showCoursesGridColumn(value);
                    }}
                    options={
                        (
                            0 === columnsToHide.length
                                ? []
                                : [{label: 'Show All', value: 'ALL'}]
                        ).concat(
                            allColumns.map(({id, accessor, Header}) => ({label: Header, value: id || accessor}))
                        )
                    }
                    {...{formatOptionLabel}}
                    styles={
                        {
                            container: (provided) => ({
                                ...provided,
                                height: '100%',
                            }),
                            control: (provided) => ({
                                ...provided,
                                height: '100%',
                            })
                        }
                    }
                />
            </div>
            <div className="text-justify mt-3 mt-md-auto ms-md-auto mb-n2">
                <button className="cec-btn cec-btn-secondary-teal me-2 mb-2" data-cy="courses--reset-table-button"
                    onClick={() => { resetCoursesGridFilterAndSort(); }}
                >
                    Reset table
                </button>
            </div>
        </div>
        <CoursesGrid columns={columnsToShow}/>
    </React.Fragment>;
};

export default connect(mapStateToProps, mapDispatchToProps)(CoursesSection);
